import React, { useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MAX_VISIBLE_CHIPS = 4;

export default function MultiSelectFilter({ placeholder = "Any", options = [], values = [], onChange, testIdPrefix = "multi-select-filter", disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => new Set(values), [values]);

  const visibleValues = useMemo(() => values.slice(0, MAX_VISIBLE_CHIPS), [values]);
  const remainingCount = Math.max(0, values.length - visibleValues.length);

  const toggle = (v) => {
    const next = new Set(values);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };

  const remove = (v) => onChange(values.filter((x) => x !== v));
  const clear = () => onChange([]);
  const selectAll = () => onChange(options);

  return (
    <Popover open={disabled ? false : open} onOpenChange={(next) => (!disabled ? setOpen(next) : null)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          className="h-auto min-h-10 w-full justify-between rounded-xl bg-white/5 px-3 py-2 text-left text-slate-100 ring-1 ring-white/10 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <div className="flex flex-1 flex-wrap gap-1 overflow-hidden">
            {values.length === 0 ? (
              <span className="text-sm text-slate-200/60">{placeholder}</span>
            ) : (
              <>
                {visibleValues.map((v, idx) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-500/20 px-2 py-1 text-xs text-indigo-100 ring-1 ring-indigo-400/20"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-chip-${idx}`}
                  >
                    <span className="max-w-[160px] truncate">{v}</span>
                    <button
                      type="button"
                      className="rounded-full p-0.5 hover:bg-indigo-500/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(v);
                      }}
                      aria-label={`Remove ${v}`}
                      data-testid={`${testIdPrefix}-remove-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {remainingCount > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-indigo-500/10 px-2 py-1 text-xs text-indigo-100/90 ring-1 ring-indigo-400/10">
                    +{remainingCount} more
                  </span>
                ) : null}
              </>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-200/70" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="z-[60] w-[340px] border-white/10 bg-slate-950 p-2 text-slate-100 shadow-2xl shadow-black/60" align="start">
        <Command className="bg-transparent text-slate-100">
          <CommandInput placeholder="Type to search…" className="text-slate-100 placeholder:text-slate-200/50" data-testid={`${testIdPrefix}-search`} />
          <div className="flex items-center justify-between gap-2 px-2 py-2 border-b border-white/10">
            <button
              type="button"
              className="rounded-full bg-indigo-500/20 px-2 py-1 text-xs font-medium text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
              onClick={() => {
                selectAll();
                setOpen(false);
              }}
              data-testid={`${testIdPrefix}-select-all`}
            >
              Select all
            </button>
            <button
              type="button"
              className="rounded-full bg-indigo-500/20 px-2 py-1 text-xs font-medium text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
              onClick={() => {
                clear();
                setOpen(false);
              }}
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear
            </button>
          </div>
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-slate-200/70">No results.</CommandEmpty>
            {options.map((opt, idx) => (
              <CommandItem
                key={opt}
                value={opt}
                onSelect={() => toggle(opt)}
                className="text-slate-100 data-[selected=true]:bg-white/10 data-[selected=true]:text-slate-100"
                data-testid={`${testIdPrefix}-option-${idx}`}
              >
                <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-sm ring-1 ring-white/20">
                  {selected.has(opt) ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="truncate">{opt}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
